import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { bulgeMidpoint, parseBulgeList } from "@dxf-ai-editor/command-engine";
import type { EncodedLeaderRoot, EncodedLeaderNode } from "./renderMultileader";
import {
  readNumber,
  readPointList,
  resolveArrayAxisAngleDeg,
  rotateOffsetAroundBaseDeg,
} from "./renderHelpers";

export type GripPoint = {
  id: string;
  entityId: string;
  x: number;
  y: number;
  propertyX: string;
  propertyY: string;
  radiusMode?: boolean;
  /**
   * Visual glyph for the grip. Defaults to a small square (undefined =
   * "square") for backward compatibility with all existing grips. Phase
   * 4 introduces "triangle" (count adjustment) and "diamond" (spacing
   * adjustment) for DXF-imported Rectangular Associative Arrays, which
   * match how AutoCAD distinguishes stretch vs. multiply grips.
   */
  gripShape?: "square" | "triangle" | "diamond";
  /**
   * Drag-constraint axis. Undefined = free 2D drag (default). Phase 4
   * uses "column" / "row" to project mouse movement along the array's
   * axisAngle-rotated column / row axis before computing the patched
   * property value. "radial" and "angular" are reserved for future
   * ARRAYPOLAR grips.
   */
  constraintAxis?: "column" | "row" | "radial" | "angular";
  /**
   * Semantic role that tells the drag handler which property to patch
   * and how to interpret the drag delta. Undefined = positional grip
   * (patch propertyX/propertyY directly — existing behaviour).
   */
  gripRole?:
    | "origin"
    | "column-count"
    | "row-count"
    | "column-spacing"
    | "row-spacing"
    | "axis-angle";
  /**
   * Rotation applied to the grip glyph (degrees, counter-clockwise in
   * world space). Only meaningful for non-square glyphs that have a
   * directional shape — triangle grips on Rectangular Associative
   * Arrays use this to keep the apex pointing along the column / row
   * axis regardless of the array's axisAngle. Square and diamond grips
   * ignore this value (both are point-symmetric under rotation or the
   * glyph itself has axis-aligned meaning). Undefined = no rotation.
   */
  gripRotationDeg?: number;
};

/* ── Phase 9 — Multileader grip helpers ───────────────── */

/**
 * Decode the JSON blob the geometry-doc enrichment pipeline writes
 * into `entity.properties.__mleader_leader_roots`. The shape mirrors
 * `encodeLeaderRoots` in dxfGeometry.ts. Returns an empty array if
 * the property is absent or malformed.
 */
export function decodeMleaderRoots(
  raw: string | undefined,
): EncodedLeaderRoot[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
    return [];
  } catch {
    return [];
  }
}

/**
 * Compute the dogleg endpoint world position for a given node, using
 * the same fallback rules as the main multileader renderer:
 *   - if the node has a doglegVector -> endpoint = lastVertex + (vec/|vec|) * length
 *   - otherwise -> horizontal segment of length `doglegLength` toward textLocation
 *
 * Returns `null` when the node has no leader lines (cannot determine
 * an anchor point).
 */
export function computeDoglegEndpoint(
  node: EncodedLeaderNode,
  doglegLength: number,
  textLocationX: number,
): { x: number; y: number } | null {
  if (node.l.length === 0) return null;
  const lastLine = node.l[node.l.length - 1];
  if (!lastLine || lastLine.v.length === 0) return null;
  const lastVert = lastLine.v[lastLine.v.length - 1];
  const lastX = lastVert[0];
  const lastY = lastVert[1];
  if (node.d) {
    const dx = node.d[0];
    const dy = node.d[1];
    const mag = Math.hypot(dx, dy) || 1;
    return {
      x: lastX + (dx / mag) * doglegLength,
      y: lastY + (dy / mag) * doglegLength,
    };
  }
  const sign = textLocationX >= lastX ? 1 : -1;
  return { x: lastX + sign * doglegLength, y: lastY };
}

/**
 * Build the two diagonal-corner grips (corner1 / corner2) shared by the
 * AABB-style entities — HATCH, VIEWPORT and Revision Cloud — whose preview
 * exposes "Corner 1 X/Y" and "Corner 2 X/Y". One-truth helper so the three
 * two-corner switch arms stay identical (drag side keys off the `corner1` /
 * `corner2` grip ids, so the field shape must not drift between them).
 */
function buildTwoCornerGrips(entity: EntityPreview): GripPoint[] {
  const p = entity.properties;
  return [
    {
      id: "corner1",
      entityId: entity.id,
      x: readNumber(p, "Corner 1 X"),
      y: readNumber(p, "Corner 1 Y"),
      propertyX: "Corner 1 X",
      propertyY: "Corner 1 Y",
    },
    {
      id: "corner2",
      entityId: entity.id,
      x: readNumber(p, "Corner 2 X"),
      y: readNumber(p, "Corner 2 Y"),
      propertyX: "Corner 2 X",
      propertyY: "Corner 2 Y",
    },
  ];
}

/* ── Grip point calculation ───────────────────────────── */

export function calculateGrips(entity: EntityPreview): GripPoint[] {
  const p = entity.properties;

  switch (entity.type) {
    case "Line": {
      // AutoCAD parity: a LINE has THREE grips — start, midpoint, end.
      // The midpoint grip translates the whole line (anchor preserves
      // both endpoints relative offsets), and is mapped to GripKind
      // "line-midpoint" in inferGripKind via the "Midpoint X" /
      // "Midpoint Y" propertyX heuristic.
      const sx = readNumber(p, "Start X");
      const sy = readNumber(p, "Start Y");
      const ex = readNumber(p, "End X");
      const ey = readNumber(p, "End Y");
      return [
        {
          id: "start",
          entityId: entity.id,
          x: sx,
          y: sy,
          propertyX: "Start X",
          propertyY: "Start Y",
        },
        {
          id: "midpoint",
          entityId: entity.id,
          x: (sx + ex) / 2,
          y: (sy + ey) / 2,
          // Midpoint grip identifies as a "Midpoint X" / "Midpoint Y"
          // pseudo-property so inferGripKind routes it to the
          // "line-midpoint" Move-X/Move-Y field set. The actual
          // commit applies the offset to both Start and End points
          // via the "move" GripCommitResult branch in
          // PrimitiveAtlasStage.tsx (special handling: midpoint move
          // translates the whole line).
          propertyX: "Midpoint X",
          propertyY: "Midpoint Y",
        },
        {
          id: "end",
          entityId: entity.id,
          x: ex,
          y: ey,
          propertyX: "End X",
          propertyY: "End Y",
        },
      ];
    }

    case "Circle": {
      const cx = readNumber(p, "Center X"),
        cy = readNumber(p, "Center Y"),
        r = readNumber(p, "Radius");
      return [
        {
          id: "center",
          entityId: entity.id,
          x: cx,
          y: cy,
          propertyX: "Center X",
          propertyY: "Center Y",
        },
        {
          id: "quad-right",
          entityId: entity.id,
          x: cx + r,
          y: cy,
          propertyX: "Center X",
          propertyY: "Center Y",
          radiusMode: true,
        },
        {
          id: "quad-top",
          entityId: entity.id,
          x: cx,
          y: cy - r,
          propertyX: "Center X",
          propertyY: "Center Y",
          radiusMode: true,
        },
        {
          id: "quad-left",
          entityId: entity.id,
          x: cx - r,
          y: cy,
          propertyX: "Center X",
          propertyY: "Center Y",
          radiusMode: true,
        },
        {
          id: "quad-bottom",
          entityId: entity.id,
          x: cx,
          y: cy + r,
          propertyX: "Center X",
          propertyY: "Center Y",
          radiusMode: true,
        },
      ];
    }

    case "Arc": {
      const cx = readNumber(p, "Center X"),
        cy = readNumber(p, "Center Y"),
        r = readNumber(p, "Radius");
      const startAngleDeg = readNumber(p, "Start angle");
      const endAngleDeg = readNumber(p, "End angle");
      const sa = (startAngleDeg * Math.PI) / 180;
      const ea = (endAngleDeg * Math.PI) / 180;
      // AutoCAD ARC midpoint grip sits on the arc itself at the angle
      // halfway between start and end. The renderer flips Y (cy - r*sin)
      // because the stage uses screen-space Y-down, so the midpoint
      // grip mirrors that convention so the glyph lands on the rendered
      // arc curve.
      const midAngleDeg =
        endAngleDeg >= startAngleDeg
          ? (startAngleDeg + endAngleDeg) / 2
          : (startAngleDeg + endAngleDeg + 360) / 2;
      const ma = (midAngleDeg * Math.PI) / 180;
      return [
        {
          id: "center",
          entityId: entity.id,
          x: cx,
          y: cy,
          propertyX: "Center X",
          propertyY: "Center Y",
        },
        {
          id: "start",
          entityId: entity.id,
          x: cx + r * Math.cos(sa),
          y: cy - r * Math.sin(sa),
          propertyX: "Start angle",
          propertyY: "Start angle",
        },
        {
          id: "end",
          entityId: entity.id,
          x: cx + r * Math.cos(ea),
          y: cy - r * Math.sin(ea),
          propertyX: "End angle",
          propertyY: "End angle",
        },
        {
          id: "arc-midpoint",
          entityId: entity.id,
          x: cx + r * Math.cos(ma),
          y: cy - r * Math.sin(ma),
          propertyX: "Center X",
          propertyY: "Center Y",
          radiusMode: true,
        },
      ];
    }

    case "Ellipse": {
      // AutoCAD ELLIPSE shows 5 grips: center + 4 axis-quadrant grips
      // (major+, major-, minor+, minor-). The major-axis endpoints sit
      // along the (Major axis X, Major axis Y) vector; the minor-axis
      // endpoints sit perpendicular to that vector with length
      // majorMag * Ratio. The renderer in renderSimpleEntities.tsx uses
      // a rotation matrix with sin = majorY/majorMag and
      // cos = majorX/majorMag to place world-space points — the grip
      // positions mirror that exact convention so they land on the
      // visual ellipse curve regardless of orientation.
      const cx = readNumber(p, "Center X");
      const cy = readNumber(p, "Center Y");
      const majorX = readNumber(p, "Major axis X");
      const majorY = readNumber(p, "Major axis Y");
      const majorZ = readNumber(p, "Major axis Z", 0);
      const ratio = readNumber(p, "Ratio", 1);
      // Direction stays XY-only (2-D screen-space placement); the minor-grip
      // DISTANCE uses the FULL 3-D major length (round 8, local Codex review
      // — mirrors `packages/cad-core/src/grips.ts::ellipseGrips`, the typed
      // render source this legacy fallback must stay consistent with, so a
      // TILTED ellipse's grip lands at the SAME distance the grip-drag
      // translator, `gripDragTranslateEllipse.ts`, treats as "no change").
      const majorMag2D = Math.hypot(majorX, majorY);
      const safeMag = majorMag2D > 0 ? majorMag2D : 1;
      const cos = majorX / safeMag;
      const sin = majorY / safeMag;
      const majorMag3D = Math.hypot(majorX, majorY, majorZ);
      const minorMag = majorMag3D * ratio;
      return [
        {
          id: "center",
          entityId: entity.id,
          x: cx,
          y: cy,
          propertyX: "Center X",
          propertyY: "Center Y",
        },
        {
          id: "major-pos",
          entityId: entity.id,
          x: cx + majorMag2D * cos,
          y: cy + majorMag2D * sin,
          propertyX: "Major axis X",
          propertyY: "Major axis Y",
        },
        {
          id: "major-neg",
          entityId: entity.id,
          x: cx - majorMag2D * cos,
          y: cy - majorMag2D * sin,
          propertyX: "Major axis X",
          propertyY: "Major axis Y",
        },
        {
          id: "minor-pos",
          entityId: entity.id,
          x: cx - minorMag * sin,
          y: cy + minorMag * cos,
          propertyX: "Ratio",
          propertyY: "Ratio",
        },
        {
          id: "minor-neg",
          entityId: entity.id,
          x: cx + minorMag * sin,
          y: cy - minorMag * cos,
          propertyX: "Ratio",
          propertyY: "Ratio",
        },
      ];
    }

    case "Text":
      return [
        {
          id: "insertion",
          entityId: entity.id,
          x: readNumber(p, "Insertion X"),
          y: readNumber(p, "Insertion Y"),
          propertyX: "Insertion X",
          propertyY: "Insertion Y",
        },
      ];

    case "MText":
      return [
        {
          id: "insertion",
          entityId: entity.id,
          x: readNumber(p, "Insertion X"),
          y: readNumber(p, "Insertion Y"),
          propertyX: "Insertion X",
          propertyY: "Insertion Y",
        },
      ];

    case "Dimension":
      return [
        {
          id: "ext1",
          entityId: entity.id,
          x: readNumber(p, "Extension 1 X"),
          y: readNumber(p, "Extension 1 Y"),
          propertyX: "Extension 1 X",
          propertyY: "Extension 1 Y",
        },
        {
          id: "ext2",
          entityId: entity.id,
          x: readNumber(p, "Extension 2 X"),
          y: readNumber(p, "Extension 2 Y"),
          propertyX: "Extension 2 X",
          propertyY: "Extension 2 Y",
        },
        {
          id: "dimline",
          entityId: entity.id,
          x: readNumber(p, "Dimension line X"),
          y: readNumber(p, "Dimension line Y"),
          propertyX: "Dimension line X",
          propertyY: "Dimension line Y",
        },
      ];

    case "Multileader":
      return [
        {
          id: "arrow",
          entityId: entity.id,
          x: readNumber(p, "Arrow X"),
          y: readNumber(p, "Arrow Y"),
          propertyX: "Arrow X",
          propertyY: "Arrow Y",
        },
        {
          id: "landing",
          entityId: entity.id,
          x: readNumber(p, "Landing X"),
          y: readNumber(p, "Landing Y"),
          propertyX: "Landing X",
          propertyY: "Landing Y",
        },
      ];

    case "Revision Cloud":
      return buildTwoCornerGrips(entity);

    case "Hatch":
      return buildTwoCornerGrips(entity);

    case "Block":
      return [
        {
          id: "insertion",
          entityId: entity.id,
          x: readNumber(p, "Insertion X"),
          y: readNumber(p, "Insertion Y"),
          propertyX: "Insertion X",
          propertyY: "Insertion Y",
        },
      ];

    case "Rectangular Associative Array":
      return buildRectangularArrayGrips(entity);

    case "Viewport":
      return buildTwoCornerGrips(entity);

    case "LWPOLYLINE":
      return buildLwpolylineGrips(entity);

    case "Spline":
      return buildSplineGrips(entity);

    default:
      return [];
  }
}

/* ── LWPOLYLINE vertex + segment-midpoint grips ───────────
 *
 * AutoCAD parity: a selected LWPOLYLINE shows ONE square vertex grip at
 * every vertex, plus ONE square midpoint grip on every segment. The
 * midpoint grip is bulge-aware — for an arc (bulge ≠ 0) segment it sits
 * on the arc bump (via the Command Engine `bulgeMidpoint` helper), not on
 * the straight chord. A closed polyline adds the implicit closing
 * segment's midpoint (last vertex → first vertex).
 *
 * Grip ids:
 *   - `vertex-<i>`  — vertex i (0-based), drag moves that vertex.
 *   - `seg-mid-<i>` — midpoint of the segment STARTING at vertex i,
 *                     drag stretches the segment by moving its midpoint
 *                     (both endpoints shift by the same delta — AutoCAD
 *                     polyline midpoint-stretch).
 *
 * The commit consumer (propertyReducer `properties/apply` → grip-drag
 * translation) reads `gripId` to know which vertex / segment changed and
 * rewrites the `Vertices` string through the Command Engine.
 */
function buildLwpolylineGrips(entity: EntityPreview): GripPoint[] {
  const verts = readPointList(entity.properties.Vertices);
  if (verts.length === 0) return [];
  const bulges = parseBulgeList(entity.properties.Bulges);
  const closedRaw = String(entity.properties.Closed ?? "")
    .trim()
    .toLowerCase();
  const closed =
    closedRaw === "true" || closedRaw === "yes" || closedRaw === "1";

  const grips: GripPoint[] = [];

  // Vertex grips.
  for (let i = 0; i < verts.length; i++) {
    const v = verts[i]!;
    grips.push({
      id: `vertex-${i}`,
      entityId: entity.id,
      x: v.x,
      y: v.y,
      propertyX: "Vertices",
      propertyY: "Vertices",
    });
  }

  // Segment-midpoint grips. Bulge i applies to the segment STARTING at
  // vertex i (AutoCAD stores bulge on the starting vertex of a segment).
  const segMid = (i: number, j: number): void => {
    const v1 = verts[i]!;
    const v2 = verts[j]!;
    const mid = bulgeMidpoint(v1, v2, bulges[i] ?? 0);
    grips.push({
      id: `seg-mid-${i}`,
      entityId: entity.id,
      x: mid.x,
      y: mid.y,
      propertyX: "Vertices",
      propertyY: "Vertices",
    });
  };
  for (let i = 0; i < verts.length - 1; i++) segMid(i, i + 1);
  if (closed && verts.length > 2) segMid(verts.length - 1, 0);

  return grips;
}

/* ── SPLINE control-point + fit-point grips ───────────────
 *
 * AutoCAD parity: a selected SPLINE shows a square grip at every control
 * point. When the spline carries fit points (fit-point spline), AutoCAD
 * ALSO shows a grip at every fit point. The control-point grips drive the
 * curve's defining polygon; the fit-point grips drive the interpolated
 * points. Both are stored as `"x,y;x,y"` strings in the preview bag.
 *
 * Grip ids:
 *   - `cp-<i>`  — control point i, drag moves it (rewrites "Control points").
 *   - `fit-<i>` — fit point i, drag moves it (rewrites "Fit points").
 */
function buildSplineGrips(entity: EntityPreview): GripPoint[] {
  const grips: GripPoint[] = [];
  const controlPoints = readPointList(entity.properties["Control points"]);
  for (let i = 0; i < controlPoints.length; i++) {
    const cp = controlPoints[i]!;
    grips.push({
      id: `cp-${i}`,
      entityId: entity.id,
      x: cp.x,
      y: cp.y,
      propertyX: "Control points",
      propertyY: "Control points",
    });
  }
  const fitPoints = readPointList(entity.properties["Fit points"]);
  for (let i = 0; i < fitPoints.length; i++) {
    const fp = fitPoints[i]!;
    grips.push({
      id: `fit-${i}`,
      entityId: entity.id,
      x: fp.x,
      y: fp.y,
      propertyX: "Fit points",
      propertyY: "Fit points",
    });
  }
  return grips;
}

/* ── Phase 4 — Rectangular Associative Array grips ───── */

/**
 * Build the 5 canonical AutoCAD-style grips for a DXF-imported
 * Rectangular Associative Array. The layout mirrors how AutoCAD draws
 * MINSERT grips:
 *
 *   1. Origin (square) at base point — moves the whole array.
 *   2. Column-count (triangle) at
 *      base + ((columns - 1) * columnSpacing, 0) rotated by axisAngle —
 *      drag along column axis adjusts Columns.
 *   3. Row-count (triangle) at
 *      base + (0, (rows - 1) * rowSpacing) rotated by axisAngle —
 *      drag along row axis adjusts Rows.
 *   4. Column-spacing (diamond) at
 *      base + (columnSpacing, 0) rotated by axisAngle — drag adjusts
 *      Column spacing.
 *   5. Row-spacing (diamond) at
 *      base + (0, rowSpacing) rotated by axisAngle — drag adjusts
 *      Row spacing.
 *
 * Rows go in +Y direction (matching renderSimpleEntities which lays
 * out copies in SVG-space without a Y-flip). Supports both legacy
 * "Origin X/Y" and newer "Base X/Y" property keys so this helper works
 * for both DXF-imported MINSERT previews and freshly created
 * associative arrays.
 */
function buildRectangularArrayGrips(entity: EntityPreview): GripPoint[] {
  const p = entity.properties;
  const hasBase = "Base X" in p || "Base Y" in p;
  const propertyX = hasBase ? "Base X" : "Origin X";
  const propertyY = hasBase ? "Base Y" : "Origin Y";
  const baseX = readNumber(p, propertyX);
  const baseY = readNumber(p, propertyY);
  const rows = Math.max(1, Math.round(readNumber(p, "Rows", 2)));
  const columns = Math.max(1, Math.round(readNumber(p, "Columns", 3)));
  const rowSpacing = readNumber(p, "Row spacing", 60);
  const columnSpacing = readNumber(p, "Column spacing", 80);
  // S-ARRAYRECT-AXISANGLE-UNIT — see renderHelpers.ts:resolveArrayAxisAngleDeg.
  // These are the LIVE canonical count/spacing grips (calculateGrips ->
  // computeGrips is the scene3d renderer's actual grip data path, per
  // computeGrips.ts's own header) — without this conversion they would
  // drift to ~1.6deg while the mesh + insertion chevrons sit at the real
  // 90deg for a real associative array (Codex P2, found in review).
  const axisAngleDeg = resolveArrayAxisAngleDeg(p);
  const rotate = (x: number, y: number) =>
    rotateOffsetAroundBaseDeg(baseX, baseY, x, y, axisAngleDeg);

  const colCountPt = rotate((columns - 1) * columnSpacing, 0);
  const rowCountPt = rotate(0, (rows - 1) * rowSpacing);
  const colSpacingPt = rotate(columnSpacing, 0);
  const rowSpacingPt = rotate(0, rowSpacing);

  return [
    {
      id: "origin",
      entityId: entity.id,
      x: baseX,
      y: baseY,
      propertyX,
      propertyY,
      gripShape: "square",
      gripRole: "origin",
    },
    {
      id: "column-count",
      entityId: entity.id,
      x: colCountPt.x,
      y: colCountPt.y,
      propertyX: "Columns",
      propertyY: "Columns",
      gripShape: "triangle",
      constraintAxis: "column",
      gripRole: "column-count",
      // Column-count triangle points in +X along the array's column
      // axis, so its glyph must rotate by axisAngle to stay aligned
      // with the row of copies that it "grows".
      gripRotationDeg: axisAngleDeg,
    },
    {
      id: "row-count",
      entityId: entity.id,
      x: rowCountPt.x,
      y: rowCountPt.y,
      propertyX: "Rows",
      propertyY: "Rows",
      gripShape: "triangle",
      constraintAxis: "row",
      gripRole: "row-count",
      // Row-count triangle points along the array's row axis (+Y in
      // the unrotated frame = axisAngle + 90°).
      gripRotationDeg: axisAngleDeg + 90,
    },
    {
      id: "column-spacing",
      entityId: entity.id,
      x: colSpacingPt.x,
      y: colSpacingPt.y,
      propertyX: "Column spacing",
      propertyY: "Column spacing",
      gripShape: "diamond",
      constraintAxis: "column",
      gripRole: "column-spacing",
    },
    {
      id: "row-spacing",
      entityId: entity.id,
      x: rowSpacingPt.x,
      y: rowSpacingPt.y,
      propertyX: "Row spacing",
      propertyY: "Row spacing",
      gripShape: "diamond",
      constraintAxis: "row",
      gripRole: "row-spacing",
    },
  ];
}
